/* ==================== F3 — Tracking de visitantes (presencia /chats) ====================
   Antes vivía INLINE en snippets/pp-visitor-tracking.liquid (contrato §4.7
   "cero requests extra"); movido a asset 2026-08-27 (perf): el bloque pesaba
   ~15 KB de HTML en CADA página y como asset viaja una vez y se cachea. El
   snippet sigue siendo el punto de entrada: emite este <script defer> con la
   config Liquid en data-attributes (sb-url, sb-anon, customer-id). defer
   conserva el orden ante assets/pp-account-track.js (defer, después en el
   DOM), que lee window._ppTrack en su primera línea.

   Brief (monorepo): docs/briefs/2026-07-01-brief-presencia-actividad-en-vivo-visitantes.md §4
   Contratos vivos que este módulo implementa (NO cambiar sin tocar el otro lado):
   - localStorage: pp_visitor_key (UUID) la crea este módulo y es la identidad PROPIA del
     visitante. NO depende del aviso de cookies (decisión del dueño, 5-oct-2026): corre
     igual con «Aceptar todo», «Solo necesarias» y sin elegir. El viejo gate
     pp_tracking_optout se retiró — con él «Solo necesarias» borraba la key y el CRM
     perdía al visitante (medido en vivo). Ver tests/identidad-propia-no-depende-del-aviso.
   - sessionStorage: pp_session_key (UUID por pestaña) + pp_session_start (ISO) —
     entered_at del payload => "tiempo en sitio" del panel del CRM.
   - Beacon page view: POST text/plain (cero preflight CORS) a track-event con
     {visitor_key, session_key, page_path, page_title, referrer}. referrer SOLO en la
     primera página de la sesión. page_path saneado de ?/# client-side (PII);
     el server re-sanea. entered_at lo pone el server.
   - Presence AUTH (2026-07-22): sesión anónima de Auth (getSession persistida o
     signInAnonymously) ANTES del subscribe — la publishable key sola NO es JWT y
     Realtime la rechaza (MalformedJWT en loop). Sin sesión, no hay subscribe.
     La identidad del visitante sigue siendo pp_visitor_key (el auth_user_id
     anónimo NO viaja en payloads ni sustituye la key).
   - GATE DE COMPROMISO (2026-08-26): signInAnonymously YA NO corre al cargar.
     Sesión persistida => subscribe igual que siempre (visitante recurrente,
     cero cambio). SIN sesión => se firma hasta que hay compromiso: 30 s de
     permanencia VISIBLE (tab oculto re-arma), carrito con items al llegar, o
     cart-update. Un rebote no minta usuario en auth.users. Porqué: el fix de
     MalformedJWT metió el sign-in en TODAS las páginas y auth.users pasó de
     195/mes a 33K/mes (~1.8K/día, 41K acumulados) — presión directa de memoria
     en la caja de la DB (postmortem monorepo 2026-08-25). Costo aceptado y
     prescrito en el handover 2026-07-22 del monorepo ("firmar tras N segundos
     de permanencia"): los rebotes <30 s sin carrito no aparecen en el panel de
     presencia del CRM. page_view/cart_snapshot NO cambian (viajan por beacon
     con visitor_key, sin Auth).
   - Presence: canal presence:v:{visitor_key} con presence.key = visitor_key. Payload:
     {visitor_key, page_path, page_title, cart_item_count, cart_total (PESOS MXN —
     /cart.js da centavos), cart_items <=3 {title, quantity}, entered_at}. track() SOLO
     tras SUBSCRIBED; coalescing >=7s (límite Realtime: 5 presence calls/cliente/30s).
   - cart-update: pub/sub del tema (assets/pubsub.js + constants.js) — publican
     product-form, cart, quick-add-bulk, quick-order-list.
   - F5 cart_snapshot: beacon a track-event {event_type:'cart_snapshot', visitor_key,
     session_key, item_count, cart_total (PESOS), items <=50 {title, variant, quantity,
     unit_price, line_price, url solo pathname}}. Gate: firma count|total en
     sessionStorage (pp_cart_sig) — solo se manda si el carrito CAMBIO. Debounce 4s en
     rafagas (quantity stepper) + flush en pagehide (el ultimo estado siempre llega —
     caso Baymard). Nunca properties/attributes/note del carrito (PII §4.6). El server
     upserta 1 fila por visitor_key (ultimo estado).
   - F6 account tracking (assets/pp-account-track.js, cargado aparte solo en no-landing):
     este módulo expone window._ppTrack = {send(event, surface, detail)} — MISMO
     visitor_key/session_key/TRACK_URL/sendBeacon-o-fetch que F2/F5, cero cliente nuevo.
     Su ausencia en window ES la señal de "no trackear" (tienda sin chat_supabase_*
     configurado, navegador sin storage o sin randomUUID) — pp-account-track.js solo tiene que checar `if (!window._ppTrack) return`.
     También expone window._ppSessionKey (visitor_key ya viajaba) y window._ppCustomerId
     (customer.id resuelto por Liquid, null si no hay sesión) para que ese módulo no
     tenga que leer nada del DOM ni duplicar la resolución de sesión.
   - Performance (§4.7): diferido a window.load + requestIdleCallback; sendBeacon; cero
     listeners de scroll/mouse/resize (pagehide es de ciclo de vida, permitido);
     delta Lighthouse LCP/TBT ~ 0. Cliente Supabase compartido via window._ppSbClient
     (cola _ppSbQueue evita doble createClient); el SDK UMD de jsdelivr se inyecta
     LAZY (solo cuando presence lo necesita, post-idle). Config Liquid desde theme
     settings (chat_supabase_url/anon_key, existen aunque el chat legacy esté apagado). */
(function () {
  'use strict';

  var cfg = document.currentScript ? document.currentScript.dataset : null;
  if (!cfg || !cfg.sbUrl || !cfg.sbAnon) return;
  const SB_URL = cfg.sbUrl;
  const SB_ANON = cfg.sbAnon;
  const CUSTOMER_ID = cfg.customerId ? Number(cfg.customerId) : null;
  const SDK = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/dist/umd/supabase.min.js';
  const TRACK_URL = SB_URL + '/functions/v1/track-event';
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const CR = window.crypto;
  let ls, ss;
  try {
    ls = window.localStorage;
    ss = window.sessionStorage;
  } catch (e) { return; }
  if (!(CR && typeof CR.randomUUID === 'function')) return;

  let visitorKey, sessionKey, sessionStart;
  let isNewSession = false;
  try {
    visitorKey = ls.getItem('pp_visitor_key');
    if (!visitorKey || !UUID_RE.test(visitorKey)) {
      visitorKey = CR.randomUUID();
      ls.setItem('pp_visitor_key', visitorKey);
    }
    sessionKey = ss.getItem('pp_session_key');
    sessionStart = ss.getItem('pp_session_start');
    if (!sessionKey || !UUID_RE.test(sessionKey)) {
      isNewSession = true;
      sessionKey = CR.randomUUID();
      ss.setItem('pp_session_key', sessionKey);
      sessionStart = null;
    }
    if (!sessionStart) {
      sessionStart = new Date().toISOString();
      ss.setItem('pp_session_start', sessionStart);
    }
  } catch (e) { return; }

  window._ppVisitorKey = visitorKey;
  window._ppSessionKey = sessionKey;
  window._ppCustomerId = CUSTOMER_ID;

  const cleanPath = (p) => {
    const q = p.indexOf('?'); if (q >= 0) p = p.slice(0, q);
    const h = p.indexOf('#'); if (h >= 0) p = p.slice(0, h);
    return p;
  };
  const pageTitle = () => (document.title || '').slice(0, 512) || null;

  // Beacon con fallback a fetch keepalive — compartido por F2/F5/F6. Sin
  // Content-Type explícito: el default de fetch con body string es
  // text/plain (CORS-safelisted, cero preflight — igual que el Blob).
  function sendBeaconOrFetch(body) {
    let sent = false;
    if (navigator.sendBeacon) {
      try { sent = navigator.sendBeacon(TRACK_URL, new Blob([body], { type: 'text/plain' })); } catch (e) { /* noop */ }
    }
    if (!sent) {
      try { fetch(TRACK_URL, { method: 'POST', body, keepalive: true }).catch(() => {}); } catch (e) { /* noop */ }
    }
  }

  function sendPageView() {
    sendBeaconOrFetch(JSON.stringify({
      visitor_key: visitorKey,
      session_key: sessionKey,
      page_path: cleanPath(location.pathname),
      page_title: pageTitle(),
      referrer: isNewSession && document.referrer ? document.referrer.slice(0, 2048) : null
    }));
  }

  // F6 — API compartida para módulos de tracking secundarios (hoy:
  // assets/pp-account-track.js, tracking del área de cuenta + bloques
  // "crea tu cuenta"). Definida hasta aquí a propósito: si los gates de
  // arriba (config, storage, randomUUID) cortaron el script antes de esta línea, window.
  // _ppTrack nunca existe — esa ausencia ES la señal de "no trackear"
  // para quien la consuma, sin tener que reimplementar el gate.
  window._ppTrack = {
    send: function (event, surface, detail) {
      sendBeaconOrFetch(JSON.stringify({
        event_type: 'account_event',
        event: String(event).slice(0, 64),
        visitor_key: visitorKey,
        session_key: sessionKey,
        shopify_customer_id: CUSTOMER_ID != null ? String(CUSTOMER_ID) : null,
        surface: surface ? String(surface).slice(0, 40) : null,
        path: cleanPath(location.pathname).slice(0, 512),
        detail: detail || null
      }));
    }
  };

  let cart = null;
  function fetchCart(cb) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js')
      .then((r) => r.json())
      .then((c) => {
        const d = c || {};
        const items = (d.items || []).slice(0, 3).map((it) => ({ title: it.product_title, quantity: it.quantity }));
        // F5: detalle completo (<=50 líneas) para el snapshot persistente.
        // Solo campos de producto — NUNCA properties/attributes/note (PII §4.6).
        const full = (d.items || []).slice(0, 50).map((it) => ({
          title: it.product_title || it.title || '',
          variant: it.variant_title || null,
          quantity: it.quantity,
          unit_price: (it.final_price != null ? it.final_price : (it.price || 0)) / 100,
          line_price: (it.final_line_price != null ? it.final_line_price : (it.line_price || 0)) / 100,
          url: it.url || null
        }));
        cart = {
          count: d.item_count || 0,
          total: (d.total_price || 0) / 100,
          items: items.length ? items : null,
          full
        };
        if (cb) cb();
      })
      .catch(() => { if (cb) cb(); });
  }

  /* F5 — snapshot persistente: manda el carrito a track-event SOLO si cambió
     (firma count|total en sessionStorage). Debounce 4s en ráfagas + flush en
     pagehide: el último estado siempre llega (caso Baymard: agregó y se fue). */
  let cartSnapTimer = null;
  function maybeSendCartSnapshot() {
    if (!cart) return;
    const sig = cart.count + '|' + cart.total;
    try {
      const prev = ss.getItem('pp_cart_sig');
      if (prev === sig) return;
      if (prev === null && cart.count === 0) { ss.setItem('pp_cart_sig', sig); return; }
      ss.setItem('pp_cart_sig', sig);
    } catch (e) { return; }
    sendBeaconOrFetch(JSON.stringify({
      event_type: 'cart_snapshot',
      visitor_key: visitorKey,
      session_key: sessionKey,
      item_count: cart.count,
      cart_total: cart.total,
      items: cart.count > 0 ? (cart.full || []) : []
    }));
  }
  function queueCartSnapshot() {
    if (cartSnapTimer) clearTimeout(cartSnapTimer);
    cartSnapTimer = setTimeout(() => { cartSnapTimer = null; maybeSendCartSnapshot(); }, 4000);
  }
  addEventListener('pagehide', () => {
    if (cartSnapTimer) {
      clearTimeout(cartSnapTimer);
      cartSnapTimer = null;
      maybeSendCartSnapshot();
    }
  });

  let channel = null;
  let lastTrack = 0;
  let trackTimer = null;
  // Gate de compromiso: startPresence lo arma cuando NO hay sesión persistida;
  // cart-update lo dispara (un visitante que toca el carrito es real).
  let engage = null;
  function fireEngage() {
    if (!engage) return;
    const e = engage;
    engage = null;
    e();
  }

  function buildPayload() {
    return {
      visitor_key: visitorKey,
      page_path: cleanPath(location.pathname),
      page_title: pageTitle(),
      cart_item_count: cart ? cart.count : null,
      cart_total: cart ? cart.total : null,
      cart_items: cart ? cart.items : null,
      entered_at: sessionStart
    };
  }
  function doTrack() {
    if (!channel) return;
    lastTrack = Date.now();
    try { channel.track(buildPayload()); } catch (e) { /* noop */ }
  }
  function throttledTrack() {
    // Coalescing >=7s entre track() (límite Realtime: 5 presence calls/30s).
    const wait = lastTrack + 7000 - Date.now();
    if (wait <= 0) { doTrack(); return; }
    if (trackTimer) return;
    trackTimer = setTimeout(() => { trackTimer = null; doTrack(); }, wait);
  }

  function withSbClient(cb) {
    if (window._ppSbClient) { cb(window._ppSbClient); return; }
    if (window._ppSbQueue) { window._ppSbQueue.push(cb); return; }
    window._ppSbQueue = [cb];
    const s = document.createElement('script');
    s.src = SDK;
    s.onload = () => {
      if (!window._ppSbClient && window.supabase) {
        window._ppSbClient = window.supabase.createClient(SB_URL, SB_ANON);
        window._ppSbLoaded = true;
      }
      const q = window._ppSbQueue;
      window._ppSbQueue = null;
      (q || []).forEach((fn) => { try { fn(window._ppSbClient); } catch (e) { /* noop */ } });
    };
    s.onerror = () => { window._ppSbQueue = null; };
    document.head.appendChild(s);
  }

  function startPresence() {
    withSbClient((client) => {
      if (!client) return;
      // Realtime exige JWT de Auth: la publishable key sola produce MalformedJWT
      // en loop de reintentos (lección 2026-07-22, ~11K errores/día). Restaura la
      // sesión anónima persistida del SDK o crea una (signInAnonymously) ANTES del
      // subscribe; el SDK auto-refresca el token y lo propaga a Realtime (setAuth).
      // Guard anti-loop: sin sesión (ok=false) NO se suscribe — misma ceguera
      // silenciosa que un fallo de red, pero cero tormenta de reconexiones.
      const go = (ok) => {
        if (!ok) return;
        try {
          channel = client.channel('presence:v:' + visitorKey, { config: { presence: { key: visitorKey } } });
          channel.subscribe((status) => {
            if (status === 'SUBSCRIBED') doTrack();
          });
        } catch (e) { /* noop */ }
      };
      const signIn = () => {
        client.auth.signInAnonymously().then(
          ({ data: d }) => go(!!(d && d.session)),
          () => go(false)
        );
      };
      try {
        client.auth.getSession().then(
          ({ data }) => {
            if (data && data.session) { go(true); return; }
            // Gate de compromiso (ver header): carrito con items = compromiso
            // inmediato; si no, 30 s de permanencia visible o cart-update.
            if (cart && cart.count > 0) { signIn(); return; }
            engage = signIn;
            const armTimer = () => {
              setTimeout(() => {
                if (!engage) return;
                if (document.hidden) { armTimer(); return; }
                fireEngage();
              }, 30000);
            };
            armTimer();
          },
          () => go(false)
        );
      } catch (e) { /* noop */ }
    });
  }

  function wireCartEvents() {
    // subscribe/PUB_SUB_EVENTS son top-level de assets/pubsub.js y
    // assets/constants.js (scripts clásicos: comparten scope). Guard typeof
    // por si el orden de carga cambia o el tema los quita.
    if (typeof subscribe !== 'function') return;
    const evt = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
    try {
      subscribe(evt, () => { fetchCart(() => { fireEngage(); throttledTrack(); queueCartSnapshot(); }); });
    } catch (e) { /* noop */ }
  }

  function start() {
    sendPageView();
    fetchCart(() => { startPresence(); maybeSendCartSnapshot(); });
    wireCartEvents();
  }
  function deferred() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 2500 });
    } else {
      setTimeout(start, 1200);
    }
  }
  if (document.readyState === 'complete') deferred();
  else addEventListener('load', deferred);
})();
